import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, Image as ImageIcon, Video, EyeOff, X, Send, MessageCircle, Loader2,
  Sparkles, MoreHorizontal, Trash2, Flag, Pin, Heart, BookHeart, Pencil,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import AuthorActionsPopover from "../components/community/AuthorActionsPopover";
import DMModal from "../components/community/DMModal";
import CommunitySidebar from "../components/community/CommunitySidebar";
import CommunityRightRail from "../components/community/CommunityRightRail";
import MagazineHome from "../components/community/MagazineHome";
import CategoryNews from "../components/community/CategoryNews";
import LoveTestCard from "../components/LoveTestCard";
import {
  CATEGORIES, FEED_TABS, topicsForCategory, topicsForSub, labelForTopic,
} from "../data/communityCategories";

import { toFlowedText } from "../utils/postText";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NEWS_CATS = ["relationships", "travel", "health", "hobby", "retirement"];

// 매거진 홈 상단 탭: 정렬(오늘의 인기/최신글) + 주요 카테고리 바로가기
const HOME_TABS = [
  { id: "popular", type: "sort", label: "오늘의 인기" },
  { id: "latest", type: "sort", label: "최신글" },
  { id: "relationships", type: "cat", label: "연애와 사랑" },
  { id: "travel", type: "cat", label: "여행" },
  { id: "health", type: "cat", label: "건강" },
  { id: "hobby", type: "cat", label: "취미" },
  { id: "retirement", type: "cat", label: "인생2막" },
];

// 글 작성 실패 사유를 명확히 안내 (모바일/인앱 브라우저 디버깅 개선)
async function describePostError(res) {
  let detail = "";
  try {
    const d = await res.json();
    detail = d?.detail || d?.message || "";
  } catch (_e) { /* 비-JSON 응답 (프록시/게이트웨이 오류 등) */ }
  if (res.status === 401)
    return "로그인이 만료되었어요. 다시 로그인한 뒤 작성해주세요.";
  if (res.status === 403)
    return detail || "작성 권한이 없어요.";
  if (res.status === 413)
    return "첨부 파일 용량이 너무 커요. 이미지/영상 크기를 줄여 다시 시도해주세요.";
  if ([502, 503, 504].includes(res.status))
    return "일시적인 서버 오류예요. 잠시 후 다시 시도해주세요.";
  return detail || `등록에 실패했어요 (오류 ${res.status}). 잠시 후 다시 시도해주세요.`;
}

// useAuth 상태가 비어있는 인앱 브라우저 대비 — localStorage 토큰 폴백
function authToken(token) {
  return token || (typeof localStorage !== "undefined" ? localStorage.getItem("liztv_token") : null);
}

// 9가지 토픽 (사용자가 요청한 섹션)
const TOPICS = [
  { id: "all", label: "전체", icon: "✨" },
  { id: "today_feeling", label: "오늘의 감정", icon: "💭" },
  { id: "today_question", label: "오늘의 질문", icon: "❓" },
  { id: "someones_story", label: "누군가의 이야기", icon: "📖" },
  { id: "lonely_night", label: "외로운 밤의 대화", icon: "🌙" },
  { id: "global_korean", label: "글로벌 한인 싱글", icon: "🌏" },
  { id: "liz_words", label: "리즈의 한마디", icon: "💌" },
  { id: "todays_empathy", label: "오늘의 공감글", icon: "🤝" },
  { id: "midlife_love", label: "중년의 사랑 이야기", icon: "❤️" },
  { id: "quiet_counsel", label: "조용한 고민상담", icon: "🕊️" },
];

// 카테고리별 작성창 인사말/질문 — 카테고리를 클릭하면 맞춤 인사가 노출됨
const CATEGORY_COMPOSER_PROMPTS = {
  intro: "반가워요! 간단한 자기소개로 첫 인사를 남겨보세요 🙂",
  relationships: "요즘 마음을 설레게 하는 사람이 있나요? 연애·관계 이야기를 들려주세요 💕",
  travel: "최근 다녀온 여행이나 가보고 싶은 곳이 있나요? 여행 이야기를 들려주세요 ✈️",
  health: "요즘 건강 관리는 어떻게 하고 계세요? 운동·식단 노하우를 나눠주세요 💪",
  hobby: "요즘 푹 빠진 취미가 있나요? 함께 즐길 이야기를 들려주세요 🎨",
  retirement: "인생 2막, 어떤 그림을 그리고 계신가요? 노후·재테크 이야기를 나눠주세요 🌅",
  free: "오늘 하루는 어떠셨어요? 편하게 일상을 들려주세요 ☕",
  today_moment: "지금 이 순간의 마음을 짧게 남겨보세요 🌿",
  anonymous_advice: "혼자 안고 있는 고민이 있나요? 익명으로 편하게 털어놓아 보세요 🕊️",
  review: "New Coupling 가입 후기를 들려주세요 ⭐",
};

// 작성창 placeholder — 홈/미지정은 기본 문구, 특정 카테고리는 맞춤 인사
function composerPlaceholder(category, username) {
  const name = username || "회원";
  const aiLine = "AI가 당신의 글을 바탕으로 더 잘 맞는 인연을 찾아드립니다.";
  const base = CATEGORY_COMPOSER_PROMPTS[category];
  if (!category || category === "home" || !base) {
    return `${name}님, 오늘의 생각과 마음을 나눠보세요.\n${aiLine}`;
  }
  return `${base}\n${aiLine}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR", { timeZone: "Asia/Seoul" });
}

// 유튜브 영상 ID 추출 (youtu.be / watch?v= / embed / shorts, ?si= 등 파라미터 허용)
function getYouTubeId(text) {
  if (!text) return null;
  const patterns = [
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/watch\?[^ ]*v=([A-Za-z0-9_-]{11})/,
    /youtube\.com\/embed\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/,
    /m\.youtube\.com\/watch\?[^ ]*v=([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m) return m[1];
  }
  return null;
}

// 본문 텍스트의 URL을 클릭 가능한 링크로 변환 + 유튜브는 아래에 임베드 플레이어 표시
function PostContent({ content }) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = (content || "").split(urlRegex);
  const ytId = getYouTubeId(content);
  const [expanded, setExpanded] = useState(false);
  const [clampable, setClampable] = useState(false);
  const pRef = useRef(null);

  useEffect(() => {
    const el = pRef.current;
    if (el && !expanded) {
      setClampable(el.scrollHeight > el.clientHeight + 4);
    }
  }, [content, expanded]);

  const clampStyle = !expanded
    ? { display: "-webkit-box", WebkitLineClamp: 10, WebkitBoxOrient: "vertical", overflow: "hidden" }
    : {};

  return (
    <>
      <p
        ref={pRef}
        style={clampStyle}
        className="text-[15px] sm:text-[16px] text-stone-800 leading-[1.65] whitespace-pre-line break-words tracking-[-0.003em]"
      >
        {parts.map((part, i) =>
          /^https?:\/\//.test(part) ? (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-burgundy-600 hover:text-burgundy-700 underline break-all"
            >
              {part}
            </a>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </p>
      {clampable && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="mt-1.5 text-sm font-semibold text-burgundy-600 hover:text-burgundy-700 transition-colors"
          data-testid="post-more-btn"
        >
          {expanded ? "접기" : "⋯ 더보기"}
        </button>
      )}
      {ytId && (
        <div className="mt-3 rounded-2xl overflow-hidden bg-black aspect-video" data-testid="post-youtube-embed">
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${ytId}`}
            title="YouTube video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      )}
    </>
  );
}


// =========================================================================
//  Daily Prompt Card — 오늘의 감정 프롬프트 (compact version)
// =========================================================================
function DailyPromptCard({ onClickWrite }) {
  const [prompt, setPrompt] = useState("");
  useEffect(() => {
    fetch(`${API_URL}/api/community/daily-prompt`).then(r => r.json()).then(d => setPrompt(d.prompt || ""));
  }, []);
  if (!prompt) return null;
  return (
    <div className="bg-gradient-to-br from-[#F5EDE5] via-[#F2EBE5] to-[#EDE3D9] rounded-2xl p-4 mb-4 border border-[#E8DCCD] relative overflow-hidden">
      <div className="flex items-start gap-3 relative">
        <Sparkles className="w-4 h-4 text-burgundy-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[10px] font-bold text-burgundy-700 tracking-[0.15em] uppercase">오늘의 질문</span>
          </div>
          <p style={{ fontWeight: 640 }} className="font-serif text-[14px] sm:text-[15px] text-stone-800 leading-snug mb-2.5">{prompt}</p>
          <button
            onClick={() => onClickWrite(prompt)}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-burgundy-700 hover:text-burgundy-800"
            data-testid="daily-prompt-write-btn"
          >
            내 이야기 들려주기 <span aria-hidden>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
//  Post Composer — 글 작성 모달
// =========================================================================
function PostComposer({ open, onClose, onCreated, prefillContent, defaultTopic, isAdmin }) {
  const { user, token } = useAuth();
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("today_feeling");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const [imagePositions, setImagePositions] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open && prefillContent) {
      // 입력 중이던 본문을 그대로 인계 (감싸지 않음)
      setContent(prefillContent);
    }
  }, [open, prefillContent]);

  // 카테고리 페이지에서 열면 해당 카테고리를 미리 선택
  useEffect(() => {
    if (open && defaultTopic) {
      setTopic(defaultTopic);
    }
  }, [open, defaultTopic]);

  const reset = () => {
    setContent(""); setTitle(""); setTopic("today_feeling"); setIsAnonymous(false); setImages([]); setImagePositions([]); setVideos([]);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files.slice(0, 4)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("file_type", "image");
        const res = await fetch(`${API_URL}/api/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.url) uploaded.push(data.url);
        else toast.error(data.detail || "이미지 업로드 실패");
      }
      setImages((prev) => [...prev, ...uploaded].slice(0, 4));
      setImagePositions((prev) => [...prev, ...uploaded.map(() => "center")].slice(0, 4));
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    // 50MB limit (server has hard limit on /api/upload via proxy)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("영상은 50MB 이하만 업로드 가능해요");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("file_type", "video");
      const res = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) {
        setVideos((prev) => [...prev, data.url].slice(0, 1));
        toast.success("영상이 추가됐어요");
      } else {
        toast.error(data.detail || "영상 업로드 실패");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error("내용을 입력해주세요");
      return;
    }
    if (!user) {
      toast.error("로그인이 필요합니다");
      return;
    }
    setBusy(true);
    try {
      const tk = authToken(token);
      if (!tk) {
        toast.error("로그인이 만료되었어요. 다시 로그인한 뒤 작성해주세요.");
        setBusy(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: JSON.stringify({
          category: "general",
          title: title.trim() || content.split("\n")[0].slice(0, 60) || labelForTopic(topic),
          content,
          images,
          image_positions: imagePositions,
          videos,
          is_anonymous: isAnonymous,
          topic,
          has_custom_title: !!title.trim(),
        }),
      });
      if (!res.ok) {
        toast.error(await describePostError(res));
        setBusy(false);
        return;
      }
      toast.success("이야기가 올라갔어요 🌿");
      reset();
      onClose();
      onCreated?.();
    } catch (e) {
      toast.error("네트워크 연결이 불안정해요. 잠시 후 다시 시도해주세요.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-stone-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={handleClose}
        >
          <motion.div
            initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col transition-colors ${
              isAnonymous ? "bg-[#FDFCFB] border border-dashed border-stone-300" : "bg-white"
            }`}
            data-testid="post-composer-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <h3 className="font-serif text-lg font-bold text-stone-900">
                {isAnonymous ? "익명으로 이야기 남기기" : "새 이야기"}
              </h3>
              <button onClick={handleClose} className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center" aria-label="닫기">
                <X className="w-4 h-4 text-stone-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
              {/* 제목 입력 — 쓰기 버튼을 눌러 이 단계에서 제목을 설정 */}
              <div>
                <div className="text-xs font-semibold text-stone-500 mb-2 tracking-wide">제목</div>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="제목을 입력해주세요 (선택)"
                  maxLength={60}
                  className="w-full text-[17px] font-bold text-stone-900 placeholder-stone-300 rounded-2xl px-4 py-3 border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-burgundy-100 focus:border-burgundy-300"
                  data-testid="composer-title"
                />
              </div>
              {/* Topic selector — 새 카테고리 시스템 */}
              <div>
                <div className="text-xs font-semibold text-stone-500 mb-2 tracking-wide">어떤 카테고리인가요?</div>
                <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-thin pr-1">
                  {CATEGORIES.filter((c) => c.isFeed && c.id !== "home" && (c.id !== "notice" || isAdmin)).map((c) => (
                    <div key={c.id}>
                      <div className="text-[11px] font-bold text-stone-400 uppercase tracking-wider mb-1.5 mt-2">{c.label}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {(c.sub && c.sub.length ? c.sub : [{ id: c.id, label: c.label }]).map((s) => (
                          <button
                            key={s.id}
                            onClick={() => setTopic(s.id)}
                            className={`px-3 py-1.5 rounded-full text-[13px] font-medium border whitespace-nowrap transition-colors ${
                              topic === s.id ? "bg-burgundy-600 text-white border-burgundy-600" : "bg-white text-stone-600 border-stone-200 hover:border-burgundy-300"
                            }`}
                            data-testid={`composer-topic-${s.id}`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="오늘 마음에 머무는 이야기를 들려주세요.&#10;짧아도 괜찮아요."
                rows={7}
                className={`w-full text-[17px] leading-[1.85] text-stone-800 placeholder-stone-400 rounded-2xl p-5 border focus:outline-none focus:ring-2 focus:ring-burgundy-100 resize-none ${
                  isAnonymous ? "bg-white border-stone-200 border-dashed" : "bg-stone-50 border-stone-200 focus:border-burgundy-300"
                }`}
                data-testid="composer-content"
              />
              {/* Image preview */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 gap-2" data-testid="composer-image-preview-grid">
                  {images.map((url, i) => {
                    const pos = imagePositions[i] || "center";
                    const posStyle = pos === "top" ? "center top" : pos === "bottom" ? "center bottom" : "center";
                    const setPos = (val) => setImagePositions((p) => { const n = [...p]; n[i] = val; return n; });
                    return (
                      <div key={i} className="relative rounded-xl overflow-hidden border border-stone-200">
                        <img src={url.startsWith("http") ? url : `${API_URL}${url}`} alt="" className="w-full h-32 object-cover" style={{ objectPosition: posStyle }} />
                        <button
                          onClick={() => { setImages((p) => p.filter((_, idx) => idx !== i)); setImagePositions((p) => p.filter((_, idx) => idx !== i)); }}
                          className="absolute top-1 right-1 w-7 h-7 rounded-full bg-stone-900/70 text-white flex items-center justify-center"
                          aria-label="이미지 제거"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                        {/* 촛점(표시) 위치 선택 */}
                        <div className="absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 bg-stone-900/60 rounded-full p-0.5 backdrop-blur-sm">
                          {[{ v: "top", l: "위" }, { v: "center", l: "가운데" }, { v: "bottom", l: "아래" }].map((o) => (
                            <button
                              key={o.v}
                              onClick={() => setPos(o.v)}
                              className={`flex-1 text-[11px] font-medium py-1 rounded-full transition-colors ${
                                pos === o.v ? "bg-white text-stone-800" : "text-white hover:bg-white/20"
                              }`}
                              data-testid={`composer-image-pos-${i}-${o.v}`}
                            >
                              {o.l}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Video preview */}
              {videos.length > 0 && (
                <div className="space-y-2">
                  {videos.map((url, i) => (
                    <div key={i} className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-900">
                      <video
                        src={url.startsWith("http") ? url : `${API_URL}${url}`}
                        controls
                        className="w-full max-h-64 object-contain"
                        data-testid={`composer-video-preview-${i}`}
                      />
                      <button
                        onClick={() => setVideos((p) => p.filter((_, idx) => idx !== i))}
                        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-stone-900/70 text-white flex items-center justify-center"
                        aria-label="영상 제거"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t border-stone-100 px-5 py-4 flex items-center gap-3 bg-stone-50/60">
              <label className="cursor-pointer w-11 h-11 rounded-full hover:bg-white flex items-center justify-center text-stone-500 border border-transparent hover:border-stone-200" title="사진 추가" data-testid="composer-image-upload">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
              </label>
              <label className="cursor-pointer w-11 h-11 rounded-full hover:bg-white flex items-center justify-center text-stone-500 border border-transparent hover:border-stone-200" title="영상 추가 (최대 50MB)" data-testid="composer-video-upload">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-5 h-5" />}
                <input type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} />
              </label>
              <button
                onClick={() => setIsAnonymous((v) => !v)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium border transition-all ${
                  isAnonymous ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-500 border-stone-200 hover:border-stone-300"
                }`}
                data-testid="composer-anonymous-toggle"
              >
                <EyeOff className="w-3.5 h-3.5" />
                익명으로
              </button>
              <div className="flex-1" />
              <button
                onClick={handleSubmit}
                disabled={busy || !content.trim()}
                className="px-6 py-3 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold text-[15px] flex items-center gap-2 disabled:opacity-50 shadow-sm transition-colors"
                data-testid="composer-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                올리기
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// =========================================================================
//  Inline Composer — 클릭/모달 없이 즉시 작성. 고급 옵션(사진·익명·카테고리)은 ⋯ 버튼.
// =========================================================================
function InlineComposer({ user, token, onCreated, onOpenAdvanced, category, sub, clearSignal }) {
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef(null);
  const composerRef = useRef(null);
  const username = user.username || (user.email ? user.email.split("@")[0] : "");
  const profileSrc = user.profile_image
    ? (user.profile_image.startsWith("http") ? user.profile_image : `${API_URL}${user.profile_image}`)
    : null;

  // 모달에서 게시가 완료되면(clearSignal 변경) 인라인 입력창을 비움
  useEffect(() => {
    if (clearSignal === undefined) return;
    setContent("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  }, [clearSignal]);

  // content가 비워질 때마다 textarea 높이 강제 리셋 (state 클리어 + height 리셋 동기화)
  useEffect(() => {
    if (content === "" && textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [content]);

  const handleSubmit = async () => {
    const text = content.trim();
    if (!text) {
      toast.error("내용을 입력해주세요");
      return;
    }
    setBusy(true);
    try {
      const tk = authToken(token);
      if (!tk) {
        toast.error("로그인이 만료되었어요. 다시 로그인한 뒤 작성해주세요.");
        setBusy(false);
        return;
      }
      // === 카테고리 자동 지정 ===
      // 1) 특정 카테고리/서브 페이지에서 작성 → 그 카테고리로 자동 지정
      // 2) 홈(전체)에서 작성 → AI가 내용을 읽고 가장 적합한 카테고리로 자동 분류
      let topic = "today_feeling";
      if (sub) {
        topic = sub;
      } else if (category && category !== "home") {
        topic = category;
      } else {
        try {
          const cres = await fetch(`${API_URL}/api/posts/classify-topic`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
            body: JSON.stringify({ content: text }),
          });
          const cdata = await cres.json().catch(() => ({}));
          if (cres.ok && cdata.topic) topic = cdata.topic;
        } catch (_e) {
          /* AI 분류 실패 시 today_feeling 폴백 */
        }
      }
      const res = await fetch(`${API_URL}/api/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: JSON.stringify({
          category: "general",
          title: text.split("\n")[0].slice(0, 60) || "오늘의 이야기",
          content: text,
          images: [],
          videos: [],
          is_anonymous: false,
          topic,
        }),
      });
      if (!res.ok) {
        toast.error(await describePostError(res));
        setBusy(false);
        return;
      }
      const isHome = !sub && (!category || category === "home");
      toast.success(isHome ? "이야기가 올라갔어요 🌿 AI가 어울리는 카테고리에 배치했어요" : "이야기가 올라갔어요 🌿");
      // 컴포저를 완전히 초기 상태로 되돌리기 (텍스트 + 높이 + 포커스 해제)
      setContent("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.blur();
      }
      // 새 글이 화면에 보이도록 부드럽게 스크롤 (피드 상단 = 컴포저 바로 아래)
      setTimeout(() => {
        if (composerRef.current) {
          const y = composerRef.current.getBoundingClientRect().bottom + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 300);
      onCreated?.();
    } catch (e) {
      toast.error("네트워크 연결이 불안정해요. 잠시 후 다시 시도해주세요.");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    // Ctrl/Cmd + Enter 로 빠른 등록
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const onTextareaInput = (e) => {
    // auto-grow up to 360px, then internal scroll
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 360)}px`;
  };

  return (
    <div
      ref={composerRef}
      className="w-full bg-white rounded-3xl p-5 sm:p-6 mb-4 border border-stone-100 hover:border-burgundy-200 shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-colors"
      data-testid="inline-composer"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Avatar — 사진이 있으면 노출, 없으면 이니셜 */}
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-burgundy-500 to-rose-500 text-white flex items-center justify-center font-bold text-xl">
          {profileSrc ? (
            <img
              src={profileSrc}
              alt={username}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="inline-composer-avatar-img"
            />
          ) : (
            <span data-testid="inline-composer-avatar-initial">{(username || "?").charAt(0).toUpperCase()}</span>
          )}
        </div>

        {/* Textarea + Toolbar */}
        <div className="flex-1 min-w-0">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onInput={onTextareaInput}
            onKeyDown={onKeyDown}
            placeholder={composerPlaceholder(category, username)}
            rows={2}
            className="w-full text-[17px] sm:text-[18px] leading-[1.7] text-stone-800 placeholder-stone-400 bg-transparent border-0 focus:outline-none resize-none overflow-y-auto"
            data-testid="inline-composer-textarea"
            disabled={busy}
          />
          {/* Toolbar — 사진/영상/카테고리 명확한 아이콘 버튼 */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onOpenAdvanced(content)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] text-stone-600 hover:text-burgundy-600 hover:bg-burgundy-50 transition-colors"
                data-testid="inline-composer-image-btn"
                title="사진 첨부"
              >
                <ImageIcon className="w-4 h-4" />
                <span className="hidden sm:inline">사진</span>
              </button>
              <button
                type="button"
                onClick={() => onOpenAdvanced(content)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] text-stone-600 hover:text-burgundy-600 hover:bg-burgundy-50 transition-colors"
                data-testid="inline-composer-video-btn"
                title="영상 첨부"
              >
                <Video className="w-4 h-4" />
                <span className="hidden sm:inline">영상</span>
              </button>
              <button
                type="button"
                onClick={() => onOpenAdvanced(content)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] text-stone-600 hover:text-burgundy-600 hover:bg-burgundy-50 transition-colors"
                data-testid="inline-composer-anon-btn"
                title="익명으로 작성"
              >
                <EyeOff className="w-4 h-4" />
                <span className="hidden sm:inline">익명</span>
              </button>
              <button
                type="button"
                onClick={() => onOpenAdvanced(content)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] text-stone-600 hover:text-burgundy-600 hover:bg-burgundy-50 transition-colors"
                data-testid="inline-composer-category-btn"
                title="카테고리 선택"
              >
                <MoreHorizontal className="w-4 h-4" />
                <span className="hidden sm:inline">카테고리</span>
              </button>
            </div>
            <button
              type="button"
              onClick={() => onOpenAdvanced(content)}
              disabled={!content.trim()}
              className="text-white font-semibold text-sm bg-burgundy-600 hover:bg-burgundy-700 disabled:bg-stone-300 disabled:cursor-not-allowed px-5 py-2 rounded-full shrink-0 transition-colors"
              data-testid="inline-composer-submit"
            >
              쓰기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// =========================================================================
//  Comment Section
// =========================================================================
function CommentSection({ postId, token, onCountChange }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/comments`);
      const data = await res.json();
      setComments(Array.isArray(data) ? data : (data.comments || []));
    } finally { setLoading(false); }
  }, [postId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const submit = async () => {
    if (!text.trim()) return;
    if (!user) { toast.error("로그인이 필요합니다"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: text, parent_id: replyTo }),
      });
      if (res.ok) {
        setText(""); setReplyTo(null);
        await fetchComments();
        onCountChange?.(1);
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "등록 실패");
      }
    } finally { setSubmitting(false); }
  };

  const tree = useMemo(() => {
    const roots = comments.filter((c) => !c.parent_id);
    return roots.map((r) => ({ ...r, replies: comments.filter((c) => c.parent_id === r.id) }));
  }, [comments]);

  return (
    <div className="bg-stone-50 px-5 py-4 border-t border-stone-100">
      {/* Composer */}
      <div className="flex gap-2 mb-4 items-end">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // 엔터 = 줄바꿈, Ctrl/⌘+Enter = 등록
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          rows={1}
          placeholder={replyTo ? "답글을 남겨주세요…" : "따뜻한 댓글을 남겨주세요… (Enter로 줄바꿈)"}
          className="flex-1 px-5 py-3 rounded-2xl bg-white border border-stone-200 text-[15px] leading-[1.6] resize-none focus:outline-none focus:border-burgundy-300 focus:ring-2 focus:ring-burgundy-100 min-h-[48px] max-h-40"
          data-testid={`comment-input-${postId}`}
        />
        <button
          onClick={submit}
          disabled={submitting || !text.trim()}
          className="px-5 py-3 rounded-full bg-burgundy-600 text-white text-[15px] font-semibold disabled:opacity-50 self-end whitespace-nowrap"
          data-testid={`comment-submit-${postId}`}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "올리기"}
        </button>
      </div>
      {loading ? (
        <div className="py-3 text-center text-xs text-stone-400">댓글 불러오는 중…</div>
      ) : tree.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-3">따뜻한 첫 댓글을 남겨주세요 🌿</p>
      ) : (
        <ul className="space-y-3">
          {tree.map((c) => (
            <li key={c.id}>
              <CommentItem c={c} onReply={() => setReplyTo(c.id)} />
              {c.replies?.length > 0 && (
                <ul className="ml-6 mt-2 space-y-2 border-l-2 border-stone-200 pl-3">
                  {c.replies.map((r) => <li key={r.id}><CommentItem c={r} compact /></li>)}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      {replyTo && (
        <button onClick={() => setReplyTo(null)} className="mt-2 text-xs text-stone-400 hover:text-stone-600">
          답글 모드 취소
        </button>
      )}
    </div>
  );
}

function CommentItem({ c, onReply, compact }) {
  return (
    <div className={`bg-white rounded-2xl p-4 border border-stone-100 ${compact ? "" : ""}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[15px] font-semibold text-stone-800">{c.author || "익명"}</span>
        <span className="text-xs text-stone-400">{timeAgo(c.created_at)}</span>
      </div>
      <p className="text-[15px] text-stone-700 leading-[1.75] whitespace-pre-wrap">{c.content}</p>
      {onReply && (
        <button onClick={onReply} className="mt-2 text-xs text-stone-400 hover:text-burgundy-600 font-medium">답글 달기</button>
      )}
    </div>
  );
}

// =========================================================================
//  Post Card
// =========================================================================
function PostCard({ post, currentUserId, token, isAdmin, onCommentCountChange, onOpenAuthor, onDeleted, onPinToggled }) {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes || 0);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || "");
  const [savingEdit, setSavingEdit] = useState(false);
  const isAnon = post.is_anonymous;
  const topicLabel = post.topic ? labelForTopic(post.topic) : null;
  const initial = (post.author || "익").charAt(0);
  const canOpenAuthor = !isAnon && post.author_id && post.author_id !== currentUserId;
  const isAuthor = !!(post.author_id && post.author_id === currentUserId);
  const canManage = isAuthor || isAdmin;
  const avatarSrc = !isAnon && post.author_profile_image
    ? (post.author_profile_image.startsWith("http") ? post.author_profile_image : `${API_URL}${post.author_profile_image}`)
    : null;

  useEffect(() => {
    setLiked(!!(currentUserId && (post.liked_by || []).includes(currentUserId)));
    setLikeCount(post.likes || 0);
  }, [post.likes, post.liked_by, currentUserId]);

  const handleLike = async () => {
    if (!currentUserId) { toast.error("로그인이 필요해요"); return; }
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount((c) => (wasLiked ? Math.max(0, c - 1) : c + 1));
    try {
      const res = await fetch(`${API_URL}/api/posts/${post.id}/like`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setLiked(data.liked); setLikeCount(data.likes); }
    } catch (e) { /* keep optimistic */ }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/community/post/${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "뉴커플링", text: (post.content || "").slice(0, 80), url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("게시글 링크를 복사했어요");
      }
    } catch (e) { /* 사용자가 공유 취소 */ }
  };

  const handleDelete = async () => {
    setMenuOpen(false);
    if (!window.confirm("이 게시글을 삭제할까요? 삭제 후에는 복구할 수 없습니다.")) return;
    try {
      const res = await fetch(`${API_URL}/api/posts/${post.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "삭제에 실패했습니다.");
      toast.success("게시글이 삭제되었어요");
      onDeleted?.(post.id);
    } catch (e) {
      toast.error(e.message || "삭제에 실패했습니다.");
    }
  };

  const handleEditStart = () => {
    setMenuOpen(false);
    setEditContent(post.content || "");
    setIsEditing(true);
  };

  const handleEditSave = async () => {
    const trimmed = editContent.trim();
    if (!trimmed) { toast.error("내용을 입력해주세요."); return; }
    setSavingEdit(true);
    try {
      const res = await fetch(`${API_URL}/api/posts/${post.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "수정에 실패했습니다.");
      post.content = trimmed; // 로컬 반영
      setIsEditing(false);
      toast.success("게시글을 수정했어요");
    } catch (e) {
      toast.error(e.message || "수정에 실패했습니다.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handlePin = async () => {
    setMenuOpen(false);
    const next = !post.is_pinned;
    try {
      const res = await fetch(`${API_URL}/api/posts/${post.id}/pin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_pinned: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "처리에 실패했습니다.");
      toast.success(next ? "상단에 고정했어요" : "고정을 해제했어요");
      onPinToggled?.(post.id, next);
    } catch (e) {
      toast.error(e.message || "처리에 실패했습니다.");
    }
  };

  const handleEssay = async () => {
    setMenuOpen(false);
    const next = !post.is_essay;
    try {
      const res = await fetch(`${API_URL}/api/admin/posts/${post.id}/toggle-essay`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_essay: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "처리에 실패했습니다.");
      post.is_essay = next; // 로컬 반영
      toast.success(next ? "‘L양의 연애 에세이’로 지정했어요" : "에세이 지정을 해제했어요");
    } catch (e) {
      toast.error(e.message || "처리에 실패했습니다.");
    }
  };

  const handleReport = async () => {
    setMenuOpen(false);
    if (!currentUserId) { toast.error("로그인이 필요해요"); return; }
    if (!post.author_id) { toast.error("신고할 수 없는 게시글이에요"); return; }
    if (!window.confirm("이 게시글을 신고할까요? 관리자가 확인 후 조치합니다.")) return;
    try {
      const res = await fetch(`${API_URL}/api/users/${post.author_id}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: "inappropriate_post", detail: `게시글 신고 (post_id: ${post.id})` }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "신고에 실패했습니다.");
      toast.success("신고가 접수되었어요");
    } catch (e) {
      toast.error(e.message || "신고에 실패했습니다.");
    }
  };

  return (
    <article
      className={`rounded-3xl mb-5 transition-colors ${
        isAnon
          ? "bg-[#FDFCFB] border border-dashed border-stone-300"
          : "bg-white border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
      }`}
      data-testid={`post-card-${post.id}`}
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-5 py-4">
        <button
          type="button"
          onClick={() => canOpenAuthor && onOpenAuthor?.(post)}
          className={`flex items-center gap-3 flex-1 min-w-0 text-left ${canOpenAuthor ? "cursor-pointer group" : "cursor-default"}`}
          data-testid={`post-author-${post.id}`}
        >
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt=""
              className={`w-12 h-12 rounded-full object-cover border border-stone-100 shrink-0 ${canOpenAuthor ? "group-hover:ring-2 group-hover:ring-burgundy-200 transition-shadow" : ""}`}
            />
          ) : (
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shrink-0 ${isAnon ? "bg-stone-400" : "bg-gradient-to-br from-burgundy-500 to-rose-500"} ${canOpenAuthor ? "group-hover:ring-2 group-hover:ring-burgundy-200 transition-shadow" : ""}`}>
              {isAnon ? "?" : initial}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`font-semibold text-stone-900 text-[15px] truncate ${canOpenAuthor ? "group-hover:text-burgundy-700" : ""}`}>
                {isAnon ? "익명의 한 분" : post.author}
              </span>
              {!isAnon && (() => {
                const m = post.author_membership;
                const isVip = m === "vip";
                const isPremium = m === "premium";
                const isRegular = m === "regular" || m === "standard" || m === "annual";
                const label = isVip ? "VIP" : isPremium ? "프리미엄" : isRegular ? "정회원" : "일반회원";
                const cls = isVip
                  ? "bg-purple-100 text-purple-700"
                  : isPremium
                    ? "bg-rose-100 text-rose-700"
                    : isRegular
                      ? "bg-rose-100 text-burgundy-700"
                      : "bg-stone-100 text-stone-500";
                return (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${cls}`} data-testid={`post-grade-${post.id}`}>
                    {label}
                  </span>
                );
              })()}
              {!isAnon && post.author_doc_verified && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 shrink-0">인증</span>
              )}
            </div>
            <div className="text-xs text-stone-400 flex items-center gap-1.5 mt-0.5">
              <span>{timeAgo(post.created_at)}</span>
              {topicLabel && <><span>·</span><span className="text-burgundy-600 font-medium">{topicLabel}</span></>}
            </div>
          </div>
        </button>
        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${menuOpen ? "bg-stone-100 text-stone-700" : "hover:bg-stone-100 text-stone-400"}`}
            title="더보기"
            aria-label="게시글 메뉴"
            data-testid={`post-menu-btn-${post.id}`}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
              <div
                className="absolute right-0 top-9 z-40 w-44 bg-white rounded-2xl border border-stone-100 shadow-lg py-1.5 overflow-hidden"
                data-testid={`post-menu-${post.id}`}
              >
                {isAdmin && (
                  <button
                    onClick={handlePin}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[14px] text-stone-700 hover:bg-stone-50 transition-colors"
                    data-testid={`post-menu-pin-${post.id}`}
                  >
                    <Pin className="w-4 h-4 text-stone-400" />
                    {post.is_pinned ? "고정 해제" : "상단 고정"}
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={handleEssay}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[14px] text-stone-700 hover:bg-stone-50 transition-colors"
                    data-testid={`post-menu-essay-${post.id}`}
                  >
                    <BookHeart className="w-4 h-4 text-burgundy-400" />
                    {post.is_essay ? "에세이 해제" : "연애 에세이로 지정"}
                  </button>
                )}
                {canManage && (
                  <button
                    onClick={handleEditStart}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[14px] text-stone-700 hover:bg-stone-50 transition-colors"
                    data-testid={`post-menu-edit-${post.id}`}
                  >
                    <Pencil className="w-4 h-4 text-stone-400" />
                    수정하기
                  </button>
                )}
                {canManage && (
                  <button
                    onClick={handleDelete}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[14px] text-red-600 hover:bg-red-50 transition-colors"
                    data-testid={`post-menu-delete-${post.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                    삭제하기
                  </button>
                )}
                {!isAuthor && (
                  <button
                    onClick={handleReport}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[14px] text-stone-700 hover:bg-stone-50 transition-colors"
                    data-testid={`post-menu-report-${post.id}`}
                  >
                    <Flag className="w-4 h-4 text-stone-400" />
                    신고하기
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </header>
      {/* Body */}
      <div className="px-5 pb-5">
        {isEditing ? (
          <div data-testid={`post-edit-area-${post.id}`}>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full min-h-[140px] rounded-2xl border border-stone-200 focus:border-burgundy-400 focus:ring-2 focus:ring-burgundy-100 outline-none p-4 text-[16px] text-stone-800 leading-[1.85] resize-y"
              data-testid={`post-edit-textarea-${post.id}`}
              autoFocus
            />
            <div className="flex items-center justify-end gap-2 mt-2.5">
              <button
                onClick={() => { setIsEditing(false); setEditContent(post.content || ""); }}
                disabled={savingEdit}
                className="px-4 py-2 rounded-full text-[14px] font-medium text-stone-500 hover:bg-stone-100 transition-colors disabled:opacity-50"
                data-testid={`post-edit-cancel-${post.id}`}
              >
                취소
              </button>
              <button
                onClick={handleEditSave}
                disabled={savingEdit}
                className="px-5 py-2 rounded-full text-[14px] font-semibold text-white bg-burgundy-600 hover:bg-burgundy-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                data-testid={`post-edit-save-${post.id}`}
              >
                {savingEdit && <Loader2 className="w-4 h-4 animate-spin" />}
                저장
              </button>
            </div>
          </div>
        ) : (
          <>
            {post.has_custom_title && post.title && (
              <h3 className="text-[18px] sm:text-[19px] font-bold text-stone-900 mb-3 leading-snug" data-testid={`post-title-${post.id}`}>
                {post.title}
              </h3>
            )}
            <PostContent content={post.content} />
          </>
        )}
      </div>
      {/* Images */}
      {post.images?.length > 0 && (
        <div className={`grid gap-1 ${post.images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {post.images.slice(0, 4).map((url, i) => {
            const pos = post.image_positions?.[i] || "center";
            const objPos = pos === "top" ? "center top" : pos === "bottom" ? "center bottom" : "center";
            return (
              <img key={i} src={url.startsWith("http") ? url : `${API_URL}${url}`} alt="" className="w-full max-h-[26rem] object-cover" style={{ objectPosition: objPos }} />
            );
          })}
        </div>
      )}
      {/* Videos */}
      {post.videos?.length > 0 && (
        <div className="bg-stone-900">
          {post.videos.slice(0, 1).map((url, i) => (
            <video
              key={i}
              src={url.startsWith("http") ? url : `${API_URL}${url}`}
              controls
              playsInline
              preload="metadata"
              className="w-full max-h-[28rem] object-contain bg-black"
              data-testid={`post-video-${post.id}-${i}`}
            />
          ))}
        </div>
      )}
      {/* Footer actions — Threads 스타일 (좋아요 / 댓글 / 전달) */}
      <div className="px-5 py-3 border-t border-stone-100 flex items-center gap-6 text-[14px] text-stone-600">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 font-medium transition-colors ${liked ? "text-rose-600" : "hover:text-rose-600"}`}
          data-testid={`post-like-${post.id}`}
        >
          <Heart className={`w-[18px] h-[18px] ${liked ? "fill-rose-600" : ""}`} strokeWidth={1.8} />
          <span>좋아요</span>
          {likeCount > 0 && <span className="tabular-nums">{likeCount}</span>}
        </button>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 hover:text-burgundy-600 font-medium transition-colors"
          data-testid={`toggle-comments-${post.id}`}
        >
          <MessageCircle className="w-[18px] h-[18px]" strokeWidth={1.8} />
          <span>댓글</span>
          {(post.comments || 0) > 0 && <span className="tabular-nums">{post.comments}</span>}
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 hover:text-burgundy-600 font-medium transition-colors"
          data-testid={`post-share-${post.id}`}
        >
          <Send className="w-[18px] h-[18px]" strokeWidth={1.8} />
          <span>전달</span>
        </button>
      </div>
      {open && <CommentSection postId={post.id} token={token} onCountChange={() => onCommentCountChange?.(post.id, 1)} />}
    </article>
  );
}

// =========================================================================
//  Main Page
// =========================================================================
export default function CommunityFeedPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("cat") || "home";
  const sub = searchParams.get("sub") || null;
  const sort = searchParams.get("sort") || "discover";

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [prefill, setPrefill] = useState("");
  const [inlineResetTick, setInlineResetTick] = useState(0);
  const [authorPopover, setAuthorPopover] = useState(null);
  const [dmTarget, setDmTarget] = useState(null);

  const isAdmin = !!(user && (user.role === "admin" || user.is_admin || user.email === "admin@newcoupling.com"));
  const noticeReadOnly = category === "notice" && !isAdmin;
  // 매거진 홈: 홈 + 기본(둘러보기) 상태일 때만 노출. 정렬/카테고리 선택 시 기존 카드 피드
  const showMagazine = category === "home" && sort === "discover";

  // 신규 회원: 가입인사를 작성해야 커뮤니티 진입 가능 (관리자 제외)
  useEffect(() => {
    if (user && user.needs_intro === true && !isAdmin) {
      navigate("/welcome-intro", { replace: true });
    }
  }, [user, isAdmin, navigate]);

  const currentCat = useMemo(() => CATEGORIES.find((c) => c.id === category) || CATEGORIES[0], [category]);
  const currentSubLabel = useMemo(() => {
    if (!sub || !currentCat?.sub) return null;
    return currentCat.sub.find((s) => s.id === sub)?.label || null;
  }, [currentCat, sub]);

  const handleSelectCategory = (catId, subId) => {
    const next = new URLSearchParams(searchParams);
    next.set("cat", catId);
    if (subId) next.set("sub", subId); else next.delete("sub");
    // 홈으로 이동 시 매거진 홈이 보이도록 정렬 초기화
    if (catId === "home") next.delete("sort");
    setSearchParams(next);
  };
  const handleSelectSort = (sortId) => {
    const next = new URLSearchParams(searchParams);
    next.set("sort", sortId);
    setSearchParams(next);
  };

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // 둘러보기는 더 큰 풀에서 섞기 위해 100개 요청
      params.set("limit", sort === "discover" ? "100" : "50");
      if (sort === "popular") params.set("sort", "popular");
      if (sort === "essay") {
        // 'L양의 연애 에세이' — 카테고리 무관, 에세이로 지정된 글 전체
        params.set("essay", "true");
      } else if (sub) {
        params.set("topics", topicsForSub(sub).join(","));
      } else if (category && category !== "home") {
        const list = topicsForCategory(category);
        if (list && list.length) params.set("topics", list.join(","));
      }
      const res = await fetch(`${API_URL}/api/posts?${params.toString()}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.posts || []);
      let normalized = list;

      // === "최신" 노출 규칙: 지난 이벤트 공지 숨김 + 최신순 정렬 ===
      if (sort === "latest") {
        const todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        normalized = normalized.filter((p) => {
          if (p.is_event_notice && p.event_date) {
            return p.event_date >= todayStr; // 행사일이 오늘 이후면 노출 (지난 이벤트 숨김)
          }
          return true;
        });
        normalized = [...normalized].sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
      }

      // === "L양의 연애 에세이": 지정된 글은 무조건 모두 노출 + 최신순 ===
      if (sort === "essay") {
        normalized = [...normalized].sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
      }

      // === 둘러보기: 매일/매번 섞기 ===
      // 1) 최근 3일 이내 글은 풀의 상위에, 그 이전 글은 풀의 하위에 배치
      // 2) Fisher-Yates 셔플(매 방문마다 새 랜덤 시드)로 각 풀 내에서 섞기
      // 3) 결과: 최근 글이 우선 노출되되, 매번 순서가 달라짐 + 오래된 글도 랜덤 추천으로 등장
      if (sort === "discover" && normalized.length > 1) {
        const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
        const now = Date.now();
        const recent = [];
        const older = [];
        normalized.forEach((p) => {
          const ts = p.created_at ? new Date(p.created_at).getTime() : 0;
          if (now - ts < THREE_DAYS) recent.push(p);
          else older.push(p);
        });
        const shuffle = (arr) => {
          for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
          }
          return arr;
        };
        shuffle(recent);
        shuffle(older);
        normalized = [...recent, ...older];
      }

      setPosts(normalized);
    } finally { setLoading(false); }
  }, [category, sub, sort, user]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // 글 작성 완료 시 — 자동으로 "최신" 탭으로 전환하여 새 글을 맨 위에서 즉시 확인 가능하게 함.
  const handlePostCreated = useCallback(() => {
    setInlineResetTick((t) => t + 1);
    if (sort !== "latest") {
      const next = new URLSearchParams(searchParams);
      next.set("sort", "latest");
      setSearchParams(next);
      // searchParams 변경 → fetchPosts 가 useEffect 로 자동 재호출
    } else {
      // 이미 최신 탭인 경우 즉시 재조회
      fetchPosts();
    }
    // 피드 상단으로 부드럽게 스크롤
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 350);
  }, [sort, searchParams, setSearchParams, fetchPosts]);

  const handleComposerOpen = (initialContent = "") => {
    if (!user) { toast.error("로그인이 필요해요"); navigate("/"); return; }
    setPrefill(initialContent);
    setComposerOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#F4F2EE] pt-20 pb-32" data-testid="community-feed-page">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex gap-5 items-start">
          {/* === Left Sidebar (desktop only — sticky while feed scrolls) === */}
          <div className="hidden lg:block sticky top-20 self-start max-h-[calc(100vh-5.5rem)] overflow-y-auto pr-1 scrollbar-thin">
            <CommunitySidebar
              activeCategory={category}
              activeSub={sub}
              onSelectCategory={handleSelectCategory}
            />
          </div>

          {/* === Main Feed === */}
          <main className="flex-1 min-w-0 max-w-2xl mx-auto lg:mx-0">
            {/* Page heading */}
            <div className="px-1 mt-3 sm:mt-5 mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="font-serif text-2xl sm:text-3xl text-stone-900 tracking-tight" data-testid="community-heading">
                  <button
                    type="button"
                    onClick={() => handleSelectCategory("home")}
                    className="text-left hover:opacity-80 transition-opacity cursor-pointer"
                    title="커뮤니티 홈(매거진)으로 이동"
                    data-testid="community-heading-home-btn"
                  >
                    {currentSubLabel
                      ? currentSubLabel
                      : currentCat.id === "home"
                        ? <><span className="font-light">공감놀이터 </span><span className="font-bold">리즈카페</span></>
                        : (currentCat.label || "공감놀이터 리즈카페")}
                  </button>
                </h1>
                <p className="text-sm text-stone-500 mt-1">
                  {currentCat.id === "home"
                    ? "중년들의 따뜻한 이야기가 모이는 곳"
                    : (currentCat.description || `${currentCat.label} 카테고리의 이야기`)}
                </p>
              </div>
            </div>

            {/* === Feed Tabs (오늘의 인기 / 최신글 / 카테고리 바로가기) === */}
            <div className="sticky top-16 z-20 -mx-3 sm:-mx-4 lg:mx-0 px-3 sm:px-4 lg:px-0 py-2 bg-[#F4F2EE]/95 backdrop-blur-sm">
              <div className="bg-white rounded-2xl border border-stone-100 p-1 flex gap-0.5 overflow-x-auto scrollbar-none shadow-[0_1px_3px_rgba(0,0,0,0.02)]" data-testid="feed-tabs">
                {HOME_TABS.map((t) => {
                  const active = t.type === "sort"
                    ? (category === "home" && sort === t.id)
                    : (category === t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => {
                        const next = new URLSearchParams(searchParams);
                        if (t.type === "sort") { next.set("cat", "home"); next.set("sort", t.id); next.delete("sub"); }
                        else { next.set("cat", t.id); next.delete("sort"); next.delete("sub"); }
                        setSearchParams(next);
                      }}
                      className={`shrink-0 px-3.5 py-2.5 rounded-xl text-[13.5px] font-semibold transition-colors whitespace-nowrap ${
                        active ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"
                      }`}
                      data-testid={`feed-tab-${t.id}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Composer — inline (no modal click needed). 고급 옵션은 ⋯ 버튼으로 모달 호출 */}
            {/* 공지 카테고리는 관리자만 작성 가능 (일반 회원은 읽기 전용) */}
            {user && !noticeReadOnly && sort !== "essay" && (
              <InlineComposer
                user={user}
                token={token}
                category={category}
                sub={sub}
                clearSignal={inlineResetTick}
                onCreated={handlePostCreated}
                onOpenAdvanced={(typed) => handleComposerOpen(typed || "")}
              />
            )}
            {noticeReadOnly && (
              <div className="bg-white rounded-2xl border border-stone-100 px-5 py-4 mb-4 text-sm text-stone-500 flex items-center gap-2" data-testid="notice-readonly-banner">
                <Sparkles className="w-4 h-4 text-burgundy-500 shrink-0" />
                공지사항은 운영팀만 작성할 수 있어요. 새로운 소식을 확인해보세요.
              </div>
            )}

            {/* 연애 역량 진단 테스트 — 홈피드 중앙 (기존 '오늘의 질문' 자리) */}
            {category === "home" && <LoveTestCard />}

            {/* Posts / Magazine Home */}
            {showMagazine ? (
              <MagazineHome />
            ) : (
              <>
                {NEWS_CATS.includes(category) && <CategoryNews category={category} />}
                {loading ? (
              <div className="py-20 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-burgundy-500" />
              </div>
            ) : posts.length === 0 ? (
              <div className="py-20 text-center" data-testid="latest-empty-state">
                {sort === "essay" ? (
                  <>
                    <p className="font-serif text-lg text-stone-600">아직 등록된 에세이가 없어요.</p>
                    <p className="text-sm text-stone-400 mt-2">곧 L양의 따뜻한 연애 에세이로 찾아올게요 🌿</p>
                  </>
                ) : (
                  <>
                    <p className="font-serif text-lg text-stone-600">아직 이야기가 없네요.</p>
                    <p className="text-sm text-stone-400 mt-2">첫 번째 이야기를 들려주시겠어요? 🌿</p>
                  </>
                )}
              </div>
            ) : (
              posts.map((p) => (
                <PostCard
                  key={p.id}
                  post={p}
                  currentUserId={user?.id}
                  token={token}
                  isAdmin={isAdmin}
                  onCommentCountChange={(postId) => setPosts((prev) => prev.map((x) => x.id === postId ? { ...x, comments: (x.comments || 0) + 1 } : x))}
                  onOpenAuthor={(post) => setAuthorPopover({ id: post.author_id, name: post.author, avatar: post.author_profile_image })}
                  onDeleted={(postId) => setPosts((prev) => prev.filter((x) => x.id !== postId))}
                  onPinToggled={(postId, pinned) => setPosts((prev) => prev.map((x) => x.id === postId ? { ...x, is_pinned: pinned } : x))}
                />
              ))
            )}
              </>
            )}
          </main>

          {/* === Right Rail (desktop only — 페이지 전체 스크롤과 함께 이동, 내부 스크롤 없음) === */}
          <div className="hidden xl:block">
            <CommunityRightRail />
          </div>
        </div>

        {/* Mobile category strip (small screens only) */}
        <div className="lg:hidden mt-6 hidden">
          {/* Mobile sidebar disabled to avoid duplicate testids; future: bottom sheet */}
        </div>
      </div>

      {/* Floating compose button (mobile-first) */}
      {user && !noticeReadOnly && (
        <button
          onClick={() => handleComposerOpen()}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105"
          aria-label="새 글 쓰기"
          data-testid="fab-compose"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Composer Modal */}
      <PostComposer
        open={composerOpen}
        prefillContent={prefill}
        isAdmin={isAdmin}
        defaultTopic={sub || (category && category !== "home" ? category : null)}
        onClose={() => { setComposerOpen(false); setPrefill(""); }}
        onCreated={handlePostCreated}
      />

      {/* Author Actions Popover */}
      <AuthorActionsPopover
        open={!!authorPopover}
        onClose={() => setAuthorPopover(null)}
        authorId={authorPopover?.id}
        authorName={authorPopover?.name}
        authorAvatar={authorPopover?.avatar}
        onOpenDM={(id, name) => setDmTarget({ id, name })}
      />

      {/* DM Modal */}
      <DMModal
        open={!!dmTarget}
        onClose={() => setDmTarget(null)}
        userId={dmTarget?.id}
        userName={dmTarget?.name}
      />
    </div>
  );
}
